<template>
  <div class="user-management" v-loading="loading">
    <div class="page-header">
      <div>
        <h2 class="page-title">用户管理</h2>
        <p class="page-desc">维护系统登录账号与密码（保存在本地 SQLite：data/market.db）</p>
      </div>
      <el-button type="primary" size="small" icon="el-icon-plus" @click="openCreateDialog">
        新增用户
      </el-button>
    </div>

    <el-table :data="users" style="width: 100%">
      <el-table-column type="index" label="序号" width="80" />
      <el-table-column prop="username" label="用户名" min-width="140" />
      <el-table-column label="角色" width="100">
        <template slot-scope="{ row }">
          <el-tag :type="row.isAdmin ? 'danger' : 'info'" size="mini">
            {{ row.isAdmin ? '管理员' : '普通用户' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="密码" min-width="160">
        <template slot-scope="{ row }">
          <span>{{ showPassword[row.username] ? row.password : '********' }}</span>
          <el-button type="text" size="mini" @click="togglePassword(row.username)">
            {{ showPassword[row.username] ? '隐藏' : '显示' }}
          </el-button>
        </template>
      </el-table-column>
      <el-table-column label="创建时间" min-width="200">
        <template slot-scope="{ row }">
          {{ formatTime(row.createdAt) }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="200" fixed="right">
        <template slot-scope="{ row }">
          <el-button type="text" size="small" @click="openEditDialog(row)">修改密码</el-button>
          <el-button
            type="text"
            size="small"
            class="danger-text"
            :disabled="users.length <= 1 || row.isAdmin"
            @click="removeUser(row)"
          >
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog
      :title="dialogMode === 'create' ? '新增用户' : '修改密码'"
      :visible.sync="dialogVisible"
      width="420px"
      @closed="resetForm"
    >
      <el-form ref="form" :model="form" :rules="rules" label-width="88px">
        <el-form-item v-if="dialogMode === 'create'" label="用户名" prop="username">
          <el-input v-model="form.username" placeholder="请输入用户名" maxlength="32" />
        </el-form-item>
        <el-form-item v-else label="用户名">
          <el-input :value="form.username" disabled />
        </el-form-item>
        <el-form-item label="密码" prop="password">
          <el-input v-model="form.password" type="password" show-password placeholder="请输入密码" />
        </el-form-item>
        <el-form-item label="确认密码" prop="confirmPassword">
          <el-input v-model="form.confirmPassword" type="password" show-password placeholder="请再次输入密码" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submitForm">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  fetchUsers,
  createUser,
  changeUserPassword,
  removeUser,
} from '../utils/userApi.js'

export default {
  name: 'UserManagement',
  data() {
    const validateConfirmPassword = (rule, value, callback) => {
      if (value !== this.form.password) {
        callback(new Error('两次输入的密码不一致'))
        return
      }
      callback()
    }
    return {
      loading: false,
      saving: false,
      users: [],
      showPassword: {},
      dialogVisible: false,
      dialogMode: 'create',
      form: {
        username: '',
        password: '',
        confirmPassword: '',
      },
      rules: {
        username: [
          { required: true, message: '请输入用户名', trigger: 'blur' },
          { min: 2, max: 32, message: '用户名长度为 2-32 个字符', trigger: 'blur' },
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' },
          { min: 6, message: '密码至少 6 位', trigger: 'blur' },
        ],
        confirmPassword: [
          { required: true, message: '请再次输入密码', trigger: 'blur' },
          { validator: validateConfirmPassword, trigger: 'blur' },
        ],
      },
    }
  },
  created() {
    this.loadUsers()
  },
  methods: {
    async loadUsers() {
      this.loading = true
      try {
        this.users = await fetchUsers()
      } catch (err) {
        this.$message.error(err.message || '加载用户失败')
      } finally {
        this.loading = false
      }
    },
    formatTime(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value
      return date.toLocaleString('zh-CN', { hour12: false })
    },
    togglePassword(username) {
      this.$set(this.showPassword, username, !this.showPassword[username])
    },
    openCreateDialog() {
      this.dialogMode = 'create'
      this.dialogVisible = true
    },
    openEditDialog(row) {
      this.dialogMode = 'edit'
      this.form.username = row.username
      this.dialogVisible = true
    },
    resetForm() {
      this.form = {
        username: '',
        password: '',
        confirmPassword: '',
      }
      if (this.$refs.form) {
        this.$refs.form.resetFields()
      }
    },
    submitForm() {
      this.$refs.form.validate(async valid => {
        if (!valid) return
        this.saving = true
        try {
          if (this.dialogMode === 'create') {
            this.users = await createUser(this.form.username, this.form.password)
            this.$message.success('用户已创建')
          } else {
            this.users = await changeUserPassword(this.form.username, this.form.password)
            this.$message.success('密码已更新')
          }
          this.dialogVisible = false
        } catch (err) {
          this.$message.error(err.message || '保存失败')
        } finally {
          this.saving = false
        }
      })
    },
    removeUser(row) {
      this.$confirm(`确认删除用户「${row.username}」？`, '提示', {
        type: 'warning',
      }).then(async () => {
        try {
          this.users = await removeUser(row.username)
          this.$message.success('用户已删除')
        } catch (err) {
          this.$message.error(err.message || '删除失败')
        }
      }).catch(() => {})
    },
  },
}
</script>

<style scoped>
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  text-align: left;
}

.page-title {
  margin: 0 0 4px;
  font-size: 20px;
  color: #303133;
}

.page-desc {
  margin: 0;
  font-size: 13px;
  color: #909399;
}

.danger-text {
  color: #f56c6c;
}
</style>
